/** @jsx jsx */
import React, { useEffect, useState } from 'react';
import { css, jsx } from '@emotion/core';
import Modal from 'components/molecules/Modal';
import PulseLoader from 'react-spinners/PulseLoader';
import StatusBadge from 'components/molecules/StatusBadge';
import { previewConfirm } from 'api/shiftBoard';
import { ConfirmPreview } from 'domain/attend/shiftBoard/types';
import { defaultTimeFormatter, shortDateLabel, TimeFormatter } from 'domain/attend/shiftBoard/time';
import { boardStyles, mock } from './styles';

const styles = {
  // 確認の結果が返るまでの表示
  loading: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    padding: '24px 16px',
    margin: '8px 0',
    border: `1px solid ${mock.gray10}`,
    borderRadius: '4px',
    background: '#FAFBFC',
  }),
  loadingTitle: css({ fontSize: '13px', fontWeight: 700, color: mock.textMain }),
};

export type ConfirmModalProps = {
  open: boolean;
  orgCode: string;
  dateFrom: string;
  dateTo: string;
  isReconfirm: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** 時間量の整形。60 進/10 進とその丸めは労務規約項目マスタに合わせる（9/23） */
  formatTime?: TimeFormatter;
};

/**
 * 確定して公開・再確定（プレビュー → 実行）
 * メールは翌朝の既存バッチが送る。ここでは送らない
 */
const ConfirmModal: React.FC<ConfirmModalProps> = ({
  open, orgCode, dateFrom, dateTo, isReconfirm, onClose, onConfirm, formatTime = defaultTimeFormatter,
}) => {
  const [preview, setPreview] = useState<ConfirmPreview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    setPreview(null);
    setError(null);
    previewConfirm({ orgCode, dateFrom, dateTo })
      .then(setPreview)
      .catch((e) => setError((e.response && e.response.data && e.response.data.message) || 'プレビューを取得できませんでした。'));
  }, [open, orgCode, dateFrom, dateTo]);

  const blocked = !!preview && preview.pendingSupportCount > 0;

  return (
    <Modal
      open={open}
      closeHandler={onClose}
      title={isReconfirm ? '再確定' : '確定して公開'}
      submitText={isReconfirm ? '再確定する' : '確定する'}
      onSubmit={() => { if (!blocked && preview) { onConfirm(); } }}
      // 確定できないときも「キャンセル」は押せるようにする（10/5）
      isDisabledSubmit={blocked || !preview}
      width="600px"
    >
      <div css={boardStyles.modalBody}>
        <div style={{ marginBottom: '8px' }}>{`${shortDateLabel(dateFrom)} 〜 ${shortDateLabel(dateTo)} のシフトを確定して公開します。公開した日はロックされ、直すときは「ロック解除」を押します。`}</div>
        {error && <div css={boardStyles.dangerText}>{error}</div>}
        {!preview && !error && (
          <div css={styles.loading} role="status" aria-live="polite">
            <PulseLoader color={mock.primary} size={10} />
            <div css={styles.loadingTitle}>対象のスタッフを確認中です</div>
          </div>
        )}
        {preview && (
          <React.Fragment>
            {preview.pendingSupportCount > 0 && <div css={boardStyles.dangerText}>{`要請中の支援シフトが ${preview.pendingSupportCount} 件あるため確定できません。`}</div>}
            <div css={boardStyles.formRow}>
              <StatusBadge tone="info" text={`通知の対象 ${preview.targets.length} 人`} />
              <StatusBadge tone="muted" text={`総労働 ${formatTime(preview.workMinutes)}`} />
              {/* 人件費は交通費を含めずに出し、交通費は別に出す。人件費率は旧画面と同じく交通費込みで計算した値（10/7） */}
              {preview.laborCost !== null && <StatusBadge tone="muted" text={`人件費 ¥${(preview.laborCost - (preview.transportation || 0)).toLocaleString()}`} />}
              {preview.laborCost !== null && !!preview.transportation && <StatusBadge tone="muted" text={`交通費 ¥${preview.transportation.toLocaleString()}`} />}
              {preview.laborCost !== null && preview.laborCostRate !== null && <StatusBadge tone="muted" text={`人件費率 ${preview.laborCostRate}%（交通費を含む）`} />}
              {preview.violationCount > 0 && <StatusBadge tone="danger" text={`アラート ${preview.violationCount} 件`} />}
            </div>
            <div css={boardStyles.subText} style={{ marginBottom: '6px' }}>
              通知は翌朝、今回確定した日にシフトのあるスタッフのうち、メールアドレスを登録している人に届きます（既存の確定通知）。
            </div>
            {preview.diffTargets.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                <div style={{ fontWeight: 600 }}>{`前回の確定から変わった内容（${preview.diffTargets.length} 人）`}</div>
                <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                  {preview.diffTargets.map((t) => (
                    <div key={t.staffCode} style={{ padding: '4px 0', borderBottom: '1px solid #EAEAEA' }}>
                      <div style={{ fontWeight: 600 }}>{t.staffName}</div>
                      {t.changes.map((c) => (
                        <div key={c.date} css={boardStyles.subText}>{`${shortDateLabel(c.date)}: ${c.before} → ${c.after}`}</div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{ marginTop: '8px', maxHeight: '160px', overflowY: 'auto' }}>
              {preview.targets.map((t) => (
                <div key={t.staffCode} css={boardStyles.subText}>{`${t.staffName}: ${t.workDays}日 ${formatTime(t.workMinutes)}`}</div>
              ))}
            </div>
          </React.Fragment>
        )}
      </div>
    </Modal>
  );
};

export default ConfirmModal;
